import type { EnrichedQuote } from '../calculations/roi/enrichQuotesWithROI';
import { getQuotePaymentOption, getQuotePayYears } from '../guidedPlanningPreferences';

type QuoteEnrichment = Pick<EnrichedQuote, 'projectedCostCoverage' | 'roi' | 'maxAchievableCoverage'>;

export type ConsumerQuote = EnrichedQuote | (TraditionalLTCQuote & QuoteEnrichment);

export const CONSUMER_PAYMENT_SCHEDULES: readonly ConsumerPaymentSchedule[] = [
  'ongoing',
  'tenPay',
  'singlePay',
];

export const DEFAULT_CONSUMER_PAYMENT_SCHEDULE: ConsumerPaymentSchedule = 'tenPay';

const TEN_PAY_YEARS = 10;

export function isTraditionalConsumerQuote(
  quote: ConsumerQuote,
): quote is TraditionalLTCQuote & QuoteEnrichment {
  return 'traditionalLTCQuoteId' in quote;
}

export function consumerQuoteKey(quote: ConsumerQuote): string {
  return isTraditionalConsumerQuote(quote)
    ? `traditional-${quote.traditionalLTCQuoteId}`
    : `hybrid-${quote.insuranceQuoteId}`;
}

export function consumerQuoteCarrier(quote: ConsumerQuote): string {
  return isTraditionalConsumerQuote(quote) ? quote.carrier : quote.carrierName;
}

export function paymentScheduleOfQuote(quote: ConsumerQuote): ConsumerPaymentSchedule | null {
  const paymentOption = getQuotePaymentOption(quote);
  if (paymentOption === 'Single Pay') return 'singlePay';
  if (paymentOption === 'Monthly') return 'ongoing';
  if (getQuotePayYears(quote) === TEN_PAY_YEARS) return 'tenPay';
  return null;
}

/** The amount shown to the consumer: monthly for ongoing, annual for 10-pay, the lump sum for single pay. */
export function consumerQuotePremium(quote: ConsumerQuote): number {
  if (isTraditionalConsumerQuote(quote)) return quote.primaryModalPremium;
  return quote.premiumAmount;
}

/** Total premiums paid, assuming an ongoing premium is paid unchanged until care starts. */
export function consumerQuoteTotalPremiums(quote: ConsumerQuote, yearsUntilCare: number): number {
  const premium = consumerQuotePremium(quote);
  switch (paymentScheduleOfQuote(quote)) {
    case 'singlePay':
      return premium;
    case 'tenPay':
      return premium * TEN_PAY_YEARS;
    default:
      return premium * 12 * Math.max(0, yearsUntilCare);
  }
}

export function isHybridConsumerQuote(quote: ConsumerQuote): quote is EnrichedQuote {
  return !isTraditionalConsumerQuote(quote);
}

export function quotesForSchedule(
  quotes: ConsumerQuote[],
  schedule: ConsumerPaymentSchedule,
): ConsumerQuote[] {
  return quotes.filter(quote => paymentScheduleOfQuote(quote) === schedule);
}

export type ConsumerOptionLabel = 'lowerPremium' | 'bestValue' | 'moreCoverage';

export interface ConsumerCoverageOption {
  quote: ConsumerQuote;
  label: ConsumerOptionLabel;
}

// A premium range from `min` up to, but not including, `max`. Within it the option is the best coverage per
// premium dollar unless `pick` asks for the cheapest.
interface PremiumBand {
  min?: number;
  max?: number;
  pick?: 'bestRoi' | 'lowestPremium';
}

/**
 * Where each option looks for its quote, by payment schedule, as premium bands tried in order.
 * Amounts are the premium as each schedule is priced: per month for ongoing, per year for
 * 10-pay, and the lump sum for single pay.
 */
const COVERAGE_OPTION_BANDS: Record<ConsumerPaymentSchedule, Record<ConsumerOptionLabel, PremiumBand[]>> = {
  ongoing: {
    lowerPremium: [{ max: 100 }, { min: 100, max: 200, pick: 'lowestPremium' }],
    bestValue: [{ min: 100, max: 200 }],
    moreCoverage: [{ min: 200 }],
  },
  tenPay: {
    lowerPremium: [{ min: 6000, max: 8000 }, { max: 10_000 }],
    bestValue: [{ min: 10_000, max: 11_000 }],
    moreCoverage: [{ min: 11_500, max: 12_500 }, { min: 11_000 }],
  },
  singlePay: {
    lowerPremium: [{ min: 45_000, max: 55_000 }, { min: 75_000, max: 95_000 }],
    bestValue: [{ min: 95_000, max: 115_000 }],
    moreCoverage: [{ min: 135_000, max: 165_000 }, { min: 115_000 }],
  },
};

const OPTION_ORDER: ConsumerOptionLabel[] = ['lowerPremium', 'bestValue', 'moreCoverage'];

function pickFromBand(
  quotes: ConsumerQuote[],
  { min = 0, max = Infinity, pick = 'bestRoi' }: PremiumBand,
): ConsumerQuote | undefined {
  const inBand = quotes.filter(
    quote => consumerQuotePremium(quote) >= min && consumerQuotePremium(quote) < max,
  );
  const ranked =
    pick === 'lowestPremium'
      ? inBand.sort((a, b) => consumerQuotePremium(a) - consumerQuotePremium(b))
      : inBand.sort(
          (a, b) => (b.roi ?? 0) - (a.roi ?? 0) || consumerQuotePremium(a) - consumerQuotePremium(b),
        );
  return ranked[0];
}

/**
 * Narrows a payment schedule's quotes to up to three options, one per premium tier, each the
 * first match in its tier's bands. A tier with no quote in any of its bands is left out, and a
 * quote already picked by a cheaper tier isn't offered twice. When no tier matches, the best
 * value quote is offered alone so the payment schedule never disappears.
 */
export function pickCoverageOptions(
  quotes: ConsumerQuote[],
  schedule: ConsumerPaymentSchedule,
): ConsumerCoverageOption[] {
  const picked: ConsumerCoverageOption[] = [];

  for (const label of OPTION_ORDER) {
    const available = quotes.filter(quote => !picked.some(option => option.quote === quote));
    const quote = COVERAGE_OPTION_BANDS[schedule][label]
      .map(band => pickFromBand(available, band))
      .find(Boolean);
    if (quote) picked.push({ quote, label });
  }

  if (picked.length || !quotes.length) return picked;
  const bestValue = pickFromBand([...quotes], {});
  return bestValue ? [{ quote: bestValue, label: 'bestValue' }] : [];
}

export function countCarriers(quotes: ConsumerQuote[]): number {
  return new Set(quotes.map(consumerQuoteCarrier)).size;
}
